import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
  Avatar,
} from '@mui/material';
import {
  ArrowBack,
  Download,
  Person,
  Phone,
  LocationOn,
  AccountBalance,
  PhotoCamera,
  Description,
  DriveEta,
  CheckCircle,
  Edit,
  ContentCopy,
  FolderShared,
  Business,
} from '@mui/icons-material';
import api from '../api';
import { getStatusColor, copyToClipboard } from '../utils';
import { useFileOperations } from '../hooks/useFileOperations';
import FilePreviewModal from '../components/FilePreviewModal';
import DocumentCard from '../components/DocumentCard';
import Card from '../components/Card';

interface Client {
  _id: string;
  trackingCode: string;
  name: string;
  email: string;
  type: string;
  modality: string;
  phoneNumber: string;
  IBAN: string;
  identityType: string;
  city: string;
  status: string;
  isReferred: boolean;
  partnerId?: any;
  rejectionReason?: string;
  verificationLink?: string;
  profilePhotoUploadLink?: string;
  idDocumentLink?: string;
  rejectedDocuments?: {
    selfie?: boolean;
    modalityPhoto?: boolean;
    driverLicense?: boolean;
    idCardFront?: boolean;
    idCardBack?: boolean;
    idCardPdf?: boolean;
    caeDocument?: boolean;
    taxClearanceDocument?: boolean;
  };
  idCard: {
    front?: string;
    back?: string;
    pdf?: string;
  };
  selfie: string;
  modalityPhoto: string;
  driverLicense?: string;
  caeDocument?: string;
  taxClearanceDocument?: string;
  googleDrive?: {
    folderId: string;
    folderUrl: string;
    uploadedFiles?: Array<{
      fileName: string;
      fileId: string;
      fileUrl: string;
    }>;
  };
  createdAt: string;
  updatedAt: string;
}

export default function ClientViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  
  const { filePreview, openFilePreview, closeFilePreview, handleDownload } = useFileOperations();

  useEffect(() => {
    fetchClient();
  }, [id]);

  const fetchClient = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const url = `/clients/${id}`;
      console.log('Fetching client from:', url, 'User role:', user.role);
      
      const response = await api.get(url);
      console.log('Client response:', response.data);
      const clientData = response.data.data;
      setClient(clientData);
    } catch (err: any) {
      console.error('Error fetching client:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.message || 'Erro ao carregar dados do cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDownload = () => {
    if (!client) return;
    
    // Download all files
    if (client.selfie) handleDownload(client.selfie, `selfie-${client.name}.png`);
    if (client.modalityPhoto) handleDownload(client.modalityPhoto, `modalidade-${client.name}.png`);
    if (client.idCard.pdf) handleDownload(client.idCard.pdf, `documento-${client.name}.pdf`);
    if (client.idCard.front) handleDownload(client.idCard.front, `documento-frente-${client.name}.png`);
    if (client.idCard.back) handleDownload(client.idCard.back, `documento-verso-${client.name}.png`);
    if (client.driverLicense) handleDownload(client.driverLicense, `cnh-${client.name}.png`);
    if (client.caeDocument) handleDownload(client.caeDocument, `cae-${client.name}.pdf`);
    if (client.taxClearanceDocument) handleDownload(client.taxClearanceDocument, `certidao-${client.name}.pdf`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Voltar
        </Button>
      </Container>
    );
  }

  if (!client) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">Cliente não encontrado</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Voltar
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: (theme) => theme.palette.grey[100], py: 3 }}>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              Detalhes do Cliente
            </Typography>
          </Box>

          {/* Client Info Card */}
          <Card sx={{ p: 4 }}>
            <Stack spacing={3}>
              {/* Header with Avatar */}
              <Stack direction="row" spacing={3} alignItems="center">
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    fontWeight: 700,
                  }}
                >
                  {client.name.charAt(0)}
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    {client.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Código: {client.trackingCode}
                  </Typography>
                  <Chip
                    label={client.status}
                    color={getStatusColor(client.status) as any}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  onClick={() => navigate(`/client/${client._id}/edit`)}
                  sx={{ fontWeight: 600 }}
                >
                  Editar Cliente
                </Button>
              </Stack>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <Person />
                      </ListItemIcon>
                      <ListItemText
                        primary="Nome"
                        secondary={client.name}
                      />
                    </ListItem>
                    {!client.isReferred && (
                      <ListItem>
                        <ListItemIcon>
                          <Phone />
                        </ListItemIcon>
                        <ListItemText
                          primary="Telefone"
                          secondary={client.phoneNumber}
                        />
                      </ListItem>
                    )}
                    {client.isReferred && (
                      <>
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircle color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Cliente Indicado"
                            secondary="Este cliente foi indicado por um parceiro"
                          />
                        </ListItem>
                        {client.partnerId && (
                          <ListItem>
                            <ListItemIcon>
                              <Business color="secondary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Parceiro"
                              secondary={`${client.partnerId.name} (${client.partnerId.partnerCode})`}
                            />
                          </ListItem>
                        )}
                      </>
                    )}
                    <ListItem>
                      <ListItemIcon>
                        <Description />
                      </ListItemIcon>
                      <ListItemText
                        primary="Email"
                        secondary={
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {client.email}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(client.email)}
                              sx={{
                                '&:hover': {
                                  background: 'rgba(102, 126, 234, 0.1)',
                                },
                              }}
                            >
                              <ContentCopy sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <LocationOn />
                      </ListItemIcon>
                      <ListItemText
                        primary="Cidade"
                        secondary={client.city}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <AccountBalance />
                      </ListItemIcon>
                      <ListItemText
                        primary="IBAN"
                        secondary={client.IBAN}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Description />
                      </ListItemIcon>
                      <ListItemText
                        primary="Tipo de Identidade"
                        secondary={
                          client.identityType === 'passport' ? 'Passaporte' :
                          client.identityType === 'cc' ? 'Cartão de cidadão (CC)' :
                          client.identityType === 'trc' ? 'Autorização de residência (TRC)' :
                          client.identityType === 'cplp' ? 'CPLP' :
                          client.identityType
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <DriveEta />
                      </ListItemIcon>
                      <ListItemText
                        primary="Tipo"
                        secondary={client.type === 'courier' ? 'Entregador' : 'Motorista'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <DriveEta />
                      </ListItemIcon>
                      <ListItemText
                        primary="Modalidade"
                        secondary={client.modality === 'bike' ? 'Bicicleta' : 'Motocicleta'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle />
                      </ListItemIcon>
                      <ListItemText
                        primary="Status"
                        secondary={client.status}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Description />
                      </ListItemIcon>
                      <ListItemText
                        primary="Data de Registro"
                        secondary={new Date(client.createdAt).toLocaleDateString('pt-BR')}
                      />
                    </ListItem>
                    {client.googleDrive && client.googleDrive.folderUrl && (
                      <ListItem>
                        <ListItemIcon>
                          <FolderShared />
                        </ListItemIcon>
                        <ListItemText
                          primary="Pasta Google Drive"
                          secondary={
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FolderShared />}
                                onClick={() => window.open(client.googleDrive!.folderUrl, '_blank')}
                                sx={{
                                  textTransform: 'none',
                                  borderColor: '#4285f4',
                                  color: '#4285f4',
                                  '&:hover': {
                                    borderColor: '#3367d6',
                                    backgroundColor: 'rgba(66, 133, 244, 0.04)',
                                  },
                                }}
                              >
                                Abrir no Google Drive
                              </Button>
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(client.googleDrive!.folderUrl)}
                                sx={{
                                  '&:hover': {
                                    background: 'rgba(66, 133, 244, 0.1)',
                                  },
                                }}
                              >
                                <ContentCopy sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          }
                        />
                      </ListItem>
                    )}
                  </List>
                </Grid>
              </Grid>

              {client.rejectionReason && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#fff3cd', borderRadius: 2, border: '1px solid #ffeaa7' }}>
                  <Typography variant="subtitle2" color="warning.dark" sx={{ fontWeight: 600, mb: 1 }}>
                    Motivo da Rejeição:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {client.rejectionReason}
                  </Typography>
                </Box>
              )}

              {client.rejectedDocuments && Object.keys(client.rejectedDocuments).some(key => client.rejectedDocuments![key as keyof typeof client.rejectedDocuments]) && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#ffebee', borderRadius: 2, border: '1px solid #ef5350' }}>
                  <Typography variant="subtitle2" color="error" sx={{ fontWeight: 600, mb: 1 }}>
                    Documentos Rejeitados:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {client.rejectedDocuments.selfie && (
                      <Typography variant="body2" color="error">• Selfie</Typography>
                    )}
                    {client.rejectedDocuments.modalityPhoto && (
                      <Typography variant="body2" color="error">• Foto da mochila</Typography>
                    )}
                    {client.rejectedDocuments.driverLicense && (
                      <Typography variant="body2" color="error">• Carta de condução</Typography>
                    )}
                    {client.rejectedDocuments.idCardFront && (
                      <Typography variant="body2" color="error">• Frente do CC/TR/CPLP</Typography>
                    )}
                    {client.rejectedDocuments.idCardBack && (
                      <Typography variant="body2" color="error">• Verso do CC/TR/CPLP</Typography>
                    )}
                    {client.rejectedDocuments.idCardPdf && (
                      <Typography variant="body2" color="error">• CC/TR/CPLP em PDF</Typography>
                    )}
                    {client.rejectedDocuments.caeDocument && (
                      <Typography variant="body2" color="error">• CAE</Typography>
                    )}
                    {client.rejectedDocuments.taxClearanceDocument && (
                      <Typography variant="body2" color="error">• Limpeza de Tributos</Typography>
                    )}
                  </Box>
                </Box>
              )}

              {client.verificationLink && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #90caf9' }}>
                  <Typography variant="subtitle2" color="info.dark" sx={{ fontWeight: 600, mb: 1 }}>
                    Link de Verificação:
                  </Typography>
                  <Typography variant="body2" color="primary">
                    <a href={client.verificationLink} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                      {client.verificationLink}
                    </a>
                  </Typography>
                </Box>
              )}

              {client.profilePhotoUploadLink && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#f3e5f5', borderRadius: 2, border: '1px solid #ce93d8' }}>
                  <Typography variant="subtitle2" color="secondary.dark" sx={{ fontWeight: 600, mb: 1 }}>
                    Link de Upload de Foto de Perfil:
                  </Typography>
                  <Typography variant="body2" color="secondary.main" sx={{ mb: 1 }}>
                    Use este link para fazer o upload da foto de perfil:
                  </Typography>
                  <Typography variant="body2" color="secondary">
                    <a href={client.profilePhotoUploadLink} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                      {client.profilePhotoUploadLink}
                    </a>
                  </Typography>
                </Box>
              )}

              {client.idDocumentLink && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#e8f5e8', borderRadius: 2, border: '1px solid #81c784' }}>
                  <Typography variant="subtitle2" color="success.dark" sx={{ fontWeight: 600, mb: 1 }}>
                    Link de Upload de Documento de Identidade:
                  </Typography>
                  <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                    Use este link para fazer o upload do documento de identidade:
                  </Typography>
                  <Typography variant="body2" color="success.dark">
                    <a href={client.idDocumentLink} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                      {client.idDocumentLink}
                    </a>
                  </Typography>
                </Box>
              )}
            </Stack>
          </Card>

          {/* Documents Section */}
          <Card sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 3 }}>
              Documentos para Download
            </Typography>

            <Grid container spacing={3}>
              {/* Selfie */}
              <Grid size={{ xs: 12, md: 6 }}>
                <DocumentCard
                  title="Selfie"
                  icon={<PhotoCamera sx={{ mr: 1, color: '#667eea' }} />}
                  filename={client.selfie}
                  onPreview={openFilePreview}
                  onDownload={handleDownload}
                  displayName={`selfie-${client.name}.png`}
                  fileType="image"
                />
              </Grid>

              {/* Modality Photo */}
              <Grid size={{ xs: 12, md: 6 }}>
                <DocumentCard
                  title="FOTO DA MOCHILA"
                  icon={<PhotoCamera sx={{ mr: 1, color: '#667eea' }} />}
                  filename={client.modalityPhoto}
                  onPreview={openFilePreview}
                  onDownload={handleDownload}
                  displayName={`modalidade-${client.name}.png`}
                  fileType="image"
                />
              </Grid>

              {/* ID Card */}
              {client.idCard.pdf ? (
                <Grid size={{ xs: 12, md: 6 }}>
                  <DocumentCard
                    title="Documento de Identidade (PDF)"
                    icon={<Description sx={{ mr: 1, color: '#667eea' }} />}
                    filename={client.idCard.pdf}
                    onPreview={openFilePreview}
                    onDownload={handleDownload}
                    displayName={`documento-${client.name}.pdf`}
                    fileType="pdf"
                  />
                </Grid>
              ) : (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <DocumentCard
                      title="Documento de Identidade (Frente)"
                      icon={<Description sx={{ mr: 1, color: '#667eea' }} />}
                      filename={client.idCard.front!}
                      onPreview={openFilePreview}
                      onDownload={handleDownload}
                      displayName={`documento-frente-${client.name}.png`}
                      fileType="image"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <DocumentCard
                      title="Documento de Identidade (Verso)"
                      icon={<Description sx={{ mr: 1, color: '#667eea' }} />}
                      filename={client.idCard.back!}
                      onPreview={openFilePreview}
                      onDownload={handleDownload}
                      displayName={`documento-verso-${client.name}.png`}
                      fileType="image"
                    />
                  </Grid>
                </>
              )}

              {/* Driver License */}
              {client.driverLicense && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <DocumentCard
                    title="Carteira de Motorista"
                    icon={<DriveEta sx={{ mr: 1, color: '#667eea' }} />}
                    filename={client.driverLicense}
                    onPreview={openFilePreview}
                    onDownload={handleDownload}
                    displayName={`cnh-${client.name}.png`}
                    fileType="image"
                  />
                </Grid>
              )}

              {/* CAE Document */}
              {client.caeDocument && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <DocumentCard
                    title="Código de Atividade Económica (CAE)"
                    icon={<Description sx={{ mr: 1, color: '#667eea' }} />}
                    filename={client.caeDocument}
                    onPreview={openFilePreview}
                    onDownload={handleDownload}
                    displayName={`cae-${client.name}.pdf`}
                    fileType="pdf"
                  />
                </Grid>
              )}

              {/* Tax Clearance Document */}
              {client.taxClearanceDocument && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <DocumentCard
                    title="Certidão de Não Dívida - Autoridade Tributária"
                    icon={<Description sx={{ mr: 1, color: '#667eea' }} />}
                    filename={client.taxClearanceDocument}
                    onPreview={openFilePreview}
                    onDownload={handleDownload}
                    displayName={`certidao-${client.name}.pdf`}
                    fileType="pdf"
                  />
                </Grid>
              )}
            </Grid>

            {/* Bulk Download */}
            <Box sx={{ mt: 4, p: 3, bgcolor: '#f8f9fa', borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Download em Lote
              </Typography>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={handleBulkDownload}
              >
                Download Todos os Documentos
              </Button>
            </Box>
          </Card>
        </Stack>
      </Container>

      {/* File Preview Modal */}
      <FilePreviewModal
        open={Boolean(filePreview)}
        onClose={closeFilePreview}
        fileUrl={filePreview?.url || null}
        fileName={filePreview?.filename || null}
        fileType={filePreview?.type || null}
        displayName={filePreview?.filename || undefined}
        onDownload={handleDownload}
      />
    </Box>
  );
} 